import { FetchError } from '@commercetools/frontend-sdk';
import { AlertCode, PSP } from 'shared/types/checkout';
import { SDKError } from 'shared/types/shared';

const getAlertCodeForPSP = <T extends PSP>(
  error: SDKError | FetchError,
  psp: T,
) => {
  switch (psp) {
    case PSP.STRIPE:
      switch (error.type) {
        case 'StripeCardError':
          return AlertCode.TRANSACTION_DECLINED;
        default:
          return AlertCode.PAYMENT_PROCESSING_FAILED;
      }
    case PSP.PAYPAL:
      switch (error.code) {
        case 'paypal_api_error':
          return AlertCode.PAYPAL_PAYMENT_PROCESSING_FAILED;
        default:
          return AlertCode.PAYMENT_PROCESSING_FAILED;
      }
    default:
      return AlertCode.PAYMENT_PROCESSING_FAILED;
  }
};

// Helper function to map general error codes to AlertCode
export const getAlertCodeFromError = (
  error: SDKError | FetchError,
): AlertCode => {
  // Check if error message contains JSON stringified error with QUOTE_EXPIRED
  if (error.message) {
    try {
      const parsedError = JSON.parse(error.message) as
        | { errorCode?: string }
        | null
        | undefined;
      if (
        parsedError &&
        typeof parsedError === 'object' &&
        'errorCode' in parsedError &&
        parsedError.errorCode === 'QUOTE_EXPIRED'
      ) {
        return AlertCode.QUOTE_EXPIRED;
      }
    } catch {
      // Not JSON, continue with normal error code check
    }
  }

  switch (error.code) {
    case 'cart_changed_error':
      return AlertCode.CART_CHANGED;
    case 'product_unavailable_error':
      return AlertCode.PRODUCT_UNAVAILABLE;
    case 'order_already_placed':
      return AlertCode.ORDER_ALREADY_PLACED;
    case 'product_unavailable_to_buy':
      return AlertCode.PRODUCT_UNAVAILABLE_TO_BUY;
    case 'QUOTE_EXPIRED':
      return AlertCode.QUOTE_EXPIRED;
    case 'upi_payment_failed':
      return AlertCode.UPI_PAYMENT_FAILED;
    default:
      return AlertCode.UNKNOWN_ERROR; // Fallback for unknown errors
  }
};

// Main function that incorporates both general and PSP-specific error mapping
export const getAlertCodeForError = <T extends PSP>(
  error: SDKError | FetchError,
  psp: T,
): AlertCode => {
  const generalAlertCode = getAlertCodeFromError(error);

  // If a general alert code is found, return it; otherwise, check PSP-specific errors
  return generalAlertCode !== AlertCode.UNKNOWN_ERROR ||
    error.code === 'product_entitlements_enrollment_fetch_error'
    ? generalAlertCode
    : getAlertCodeForPSP(error, psp);
};

// AlertCodes that represent a payment/quote failure already being shown to
// the shopper. A lower-priority, automatically-triggered alert (e.g. the
// currency-lock banner that fires once FX rate loading completes) must not
// silently replace one of these — the checkout-stepper and
// checkout-simplified variants both guard their currency-alert effects
// with this before dispatching.
const BLOCKING_CHECKOUT_ALERT_CODES: ReadonlySet<AlertCode> = new Set([
  AlertCode.QUOTE_EXPIRED,
  AlertCode.RATES_UNAVAILABLE,
  AlertCode.PAYMENT_PROCESSING_FAILED,
  AlertCode.UPI_PAYMENT_FAILED,
]);

export const isBlockingCheckoutAlert = (
  alertCode: AlertCode | null | undefined,
): boolean => !!alertCode && BLOCKING_CHECKOUT_ALERT_CODES.has(alertCode);
